import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { onRefresh } from './cache'

// Runs `task` once at mount, then every `ms` while the tab is visible; a hidden tab stops polling and catches up when shown again.
export const usePolling = (task, ms, deps = []) => {
  const taskRef = useRef(task)
  taskRef.current = task

  useEffect(() => {
    let timer = null
    let inFlight = false
    const run = async () => {
      if (inFlight) return // a slow backend must not stack requests
      inFlight = true
      try { await taskRef.current() } finally { inFlight = false }
    }
    const start = (refresh = true) => { if (!timer) { if (refresh) run(); timer = setInterval(run, ms) } }
    const stop = () => { clearInterval(timer); timer = null }
    const onVisibility = () => (document.hidden ? stop() : start())

    run() // a page opened in a background tab still has its figures ready when it is shown
    if (!document.hidden) start(false)
    document.addEventListener('visibilitychange', onVisibility)
    const offRefresh = onRefresh(run) // the refresh button: poll now
    return () => { stop(); offRefresh(); document.removeEventListener('visibilitychange', onVisibility) }
  }, [ms, ...deps])
}

// Charts and transitions stay still for people who asked their system for less motion.
export const usePrefersReducedMotion = () => {
  const query = '(prefers-reduced-motion: reduce)'
  const [reduced, setReduced] = useState(() => typeof window !== 'undefined' && window.matchMedia?.(query).matches)

  useEffect(() => {
    const media = window.matchMedia?.(query)
    if (!media) return
    const onChange = () => setReduced(media.matches)
    media.addEventListener?.('change', onChange)
    return () => media.removeEventListener?.('change', onChange)
  }, [])

  return reduced
}

// useTranslation for the B-book screens: React already escapes what it renders, and i18next escaping on top
// turns "XAU/USD" into "XAU&#x2F;USD" and "D'Arc" into "D&#39;Arc". Options can still override it.
export const useT = () => {
  const { t, i18n } = useTranslation()
  return { i18n, t: (key, options) => t(key, { interpolation: { escapeValue: false }, ...options }) }
}
